import { cloneDeep } from 'lodash';
import { createFontStyle } from '@/framework';
import { loadImageAsBase64 } from '@/utils/base64-kit';
import { loadSvgAsync, serializerDomToString } from '@/utils/file-kit';
import { getLayerFreshDOM } from '@/utils/layer-kit';
import { transformLowLeverTextDataToCss } from '@/utils/transform-kit';
import { TextWritingType, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { renderDom } from '../view/render-dom';
import { getTextureUrl } from './resource';

/**
 * 文本图层 svg 转换工具
 */
export class SVGTransformer {
    private layer: ILayerProps;
    private layerData: ITextLayerProps;

    private fontSubsetToBase64: Record<string, string> = {};
    private fontFamilyStyles: string[] = [];

    constructor(
        private identity: string,
        layer: ILayerProps,
        fontSubsetToBase64: Record<string, string> = {},
    ) {
        this.layer = layer;
        this.layerData = layer.data as ITextLayerProps;

        this.fontSubsetToBase64 = fontSubsetToBase64;

        this.initFontFamilyStyles();
    }

    /**
     * 初始化字体样式
     */
    private initFontFamilyStyles() {
        const fontSubset = Object.keys(this.fontSubsetToBase64);

        fontSubset.forEach((fontFamily) => {
            const base64 = this.fontSubsetToBase64[fontFamily];
            if (base64) {
                const fontFamilyStyle = createFontStyle(fontFamily, base64, true);
                this.fontFamilyStyles.push(fontFamilyStyle);
            }
        });
    }

    /**
     * 获取 svg 尺寸
     * 注意点：
     *  - 需要预留特效的占位尺寸
     * @returns
     */
    private getSVGSize() {
        const { width: layerWidth, height: layerHeight } = this.layer;
        const { writingMode, fontSize, textStroke = [], textShadow = [], textGlow = [] } = this.layerData;

        const effects = [...textShadow, ...textStroke, ...textGlow];
        const effectsWidth = Math.round(effects.length * (fontSize / 2));

        // 预留尺寸（ATTENTION: 默认值为主观定义）
        let preserveWidth = fontSize;
        let preserveHeight = fontSize * 3;

        if (writingMode === TextWritingType.HORIZONTAL_TB) {
            preserveWidth = Math.max(preserveWidth, effectsWidth);
        } else {
            preserveHeight = Math.max(preserveHeight, effectsWidth);
        }

        const width = layerWidth + preserveWidth;
        const height = layerHeight + preserveHeight;

        return { width, height };
    }

    private async getLayerContentDOM() {
        const selector = `[data-draw-id=text-${this.layer.id}]`;
        const layer = cloneDeep(this.layer);
        const layerData = transformLowLeverTextDataToCss(layer.data as ITextLayerProps);

        layer.data = layerData;

        const dispose = await renderDom(this.identity, layer);
        const $dom = getLayerFreshDOM(selector);

        dispose?.();

        return $dom;
    }

    private async getLayerContentDOMString($dom: HTMLElement) {
        const $cloned = $dom.cloneNode(true) as HTMLElement;

        // --- 替换纹理图片 url ---
        const { texture } = this.layerData;
        if (texture && texture.url) {
            const url = getTextureUrl(texture.url);
            const base64 = (await loadImageAsBase64(url)) as string;
            if (base64) {
                const $patternImage = $cloned.querySelector(`#texture-image-${this.layer.id}`);
                if ($patternImage) {
                    $patternImage.setAttribute('href', base64);
                }

                // HACK: 由于竖排文字的纹理是单独使用 image 标签进行以 clipPath 的方式渲染，所以出图时也要兼顾这种方式
                const $verticalTextureImages = $cloned.querySelectorAll(
                    `[data-texture-id^=texture-image-vertical-${this.layer.id}]`,
                );
                $verticalTextureImages.forEach(($image) => {
                    $image.setAttribute('href', base64);
                });
            }
        }

        // --- 替换列表样式图片 url ---
        const { listStyleMaterial } = this.layerData;
        if (listStyleMaterial && !listStyleMaterial.url.endsWith('.svg')) {
            const url = listStyleMaterial.url;
            const base64 = (await loadImageAsBase64(url)) as string;
            if (base64) {
                const $image = $cloned.querySelector(`#list-marker-image-typed-${this.layer.id}`);
                if ($image) $image.setAttribute('href', base64);
            }
        }

        const domString = serializerDomToString($cloned);

        return domString;
    }

    private async getSVGDataURL() {
        const $dom = await this.getLayerContentDOM();
        if (!$dom) return '';

        const domString = await this.getLayerContentDOMString($dom);

        const { width: layerWidth, height: layerHeight } = this.layer;
        const { width: svgWidth, height: svgHeight } = this.getSVGSize();
        const preserveWidth = svgWidth - layerWidth;
        const preserveHeight = svgHeight - layerHeight;

        const content = `
            <div style="
                position:absolute;
                left: 0;
                top: 0;
                width:${svgWidth}px;
                height:${svgHeight}px;
            ">
                <div style="
                    width:100%;
                    height:100%;
                    position:absolute;
                    left:${preserveWidth / 2}px;
                    top:${preserveHeight / 2}px;
                ">
                    ${domString}
                </div>
            </div>
        `;

        const injectStyle = `
            <style type="text/css">
                ${this.fontFamilyStyles.join('')}
            </style>
        `;

        const svg = `
            <svg
                width="${svgWidth}"
                height="${svgHeight}"
                viewBox="0 0 ${svgWidth} ${svgHeight}"
                xmlns="http://www.w3.org/2000/svg"
            >
                ${injectStyle}
                <foreignObject x="0" y="0" width="100%" height="100%">
                    <body xmlns="http://www.w3.org/1999/xhtml">
                        ${content}
                    </body>
                </foreignObject>
            </svg>
        `;

        const dataURL = `data:image/svg+xml;charset=utf-8,${svg}`;

        return dataURL;
    }

    public async transform() {
        const dataURL = await this.getSVGDataURL();
        if (!dataURL) return null;

        const blob = new Blob([dataURL], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);

        try {
            // HACK: 经询问，这里加载两次是因为 safari 下会出现字体丢失的情况
            const svg = await loadSvgAsync(dataURL);
            const image = await loadSvgAsync(svg.src);

            return {
                image,
                url,
                width: image.width,
                height: image.height,
            };
        } catch (_) {
            console.log('SVGTransformer - 文字图层出图失败: ', dataURL);
            return null;
        }
    }
}
